/**
 * Lesson Date Utilities
 * Domain logic for generating individual lesson occurrence dates from term schedules
 */

import type { CoachAvailabilityException, Lesson, LessonOverride, Term } from '../types';
import { doTimesOverlap } from './timeOverlap';

export interface LessonOccurrence {
  date: Date;
  dayOfWeek: number;
  lessonId: string;
  startTime: string;
  endTime: string;
  status: 'scheduled' | 'cancelled' | 'postponed';
  override?: LessonOverride;
  /** Set when occurrences are loaded from materialized lesson_sessions */
  id?: string;
}

/** Payload for inserting lesson_sessions rows (no id/timestamps). */
export interface LessonSessionInsert {
  lessonId: string;
  academyId: string;
  sessionDate: string;
  startTime: string;
  endTime: string;
}

type DateLike = Date | string | { toDate: () => Date };

const ISO_DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Parse a YYYY-MM-DD string as local calendar midnight (avoids UTC shift from `new Date('YYYY-MM-DD')`).
 */
export function parseLocalDateYmd(value: string): Date | null {
  const m = ISO_DATE_ONLY.exec(value.trim());
  if (!m) return null;
  const y = Number(m[1]);
  const month = Number(m[2]) - 1;
  const day = Number(m[3]);
  const d = new Date(y, month, day);
  if (d.getFullYear() !== y || d.getMonth() !== month || d.getDate() !== day) {
    return null;
  }
  return d;
}

/** Format a Date or date-only string for HTML `<input type="date">` (local calendar day). */
export function formatDateToFormYmd(value: DateLike | undefined | null): string {
  if (value == null) return '';
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (ISO_DATE_ONLY.test(trimmed)) return trimmed;
    const local = parseLocalDateYmd(trimmed);
    if (local) return formatLessonOccurrenceDateYmd(local);
    const parsed = new Date(trimmed);
    return Number.isNaN(parsed.getTime()) ? '' : formatLessonOccurrenceDateYmd(parsed);
  }
  if (value instanceof Date) {
    return formatLessonOccurrenceDateYmd(value);
  }
  if (
    typeof value === 'object' &&
    'toDate' in value &&
    typeof (value as { toDate: () => Date }).toDate === 'function'
  ) {
    const d = (value as { toDate: () => Date }).toDate();
    return formatLessonOccurrenceDateYmd(d);
  }
  return '';
}

/** Format a local calendar date as YYYY-MM-DD (matches lesson_sessions.session_date keys). */
export function formatLessonOccurrenceDateYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatYmd(d: Date): string {
  return formatLessonOccurrenceDateYmd(d);
}

/**
 * Compute session rows to persist for a group or camp lesson over a date range (term or camp dates).
 */
export function computeSessionsForDateRange(
  lesson: Pick<Lesson, 'id' | 'academyId' | 'daysOfWeek' | 'startTime' | 'endTime'>,
  dateRange: Pick<Term, 'startDate' | 'endDate'>
): LessonSessionInsert[] {
  const occ = generateLessonOccurrences(lesson, dateRange, []);
  return occ.map((o) => ({
    lessonId: lesson.id,
    academyId: lesson.academyId,
    sessionDate: formatYmd(o.date),
    startTime: lesson.startTime,
    endTime: lesson.endTime,
  }));
}

/**
 * Compute session rows for a private lesson (startDate + numberOfSessions + optional daysOfWeek).
 */
export function computeSessionsForPrivateLesson(
  lesson: Pick<Lesson, 'id' | 'academyId' | 'startTime' | 'endTime'>,
  startDate: DateLike,
  numberOfSessions: number,
  daysOfWeek?: number[] | null
): LessonSessionInsert[] {
  const dates = generatePrivateLessonOccurrenceDates(startDate, numberOfSessions, daysOfWeek);
  return dates.map((d) => ({
    lessonId: lesson.id,
    academyId: lesson.academyId,
    sessionDate: formatYmd(d),
    startTime: lesson.startTime,
    endTime: lesson.endTime,
  }));
}

function toDate(value: DateLike | undefined): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'string') {
    const local = parseLocalDateYmd(value);
    if (local) return local;
    return new Date(value);
  }
  if (
    typeof value === 'object' &&
    value !== null &&
    'toDate' in value &&
    typeof (value as { toDate: () => Date }).toDate === 'function'
  ) {
    return (value as { toDate: () => Date }).toDate();
  }
  try {
    return new Date(value as unknown as string);
  } catch {
    return null;
  }
}

/**
 * Compare two dates by date part only (ignore time)
 */
function isSameDate(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** Advance to the first date on or after `start` whose weekday is in `days`. */
function firstDateOnOrAfterMatchingDays(start: Date, days: number[]): Date {
  const set = new Set(days);
  const d = new Date(start);
  d.setHours(0, 0, 0, 0);
  for (let guard = 0; guard < 400; guard++) {
    if (set.has(d.getDay())) {
      return new Date(d);
    }
    d.setDate(d.getDate() + 1);
  }
  return new Date(start);
}

/** Earliest date strictly after `from` (same calendar rules) that falls on a weekday in `daySet`. */
function nextDateAfterMatchingDays(from: Date, daySet: Set<number>): Date {
  const d = new Date(from);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 1);
  for (let guard = 0; guard < 400; guard++) {
    if (daySet.has(d.getDay())) {
      return d;
    }
    d.setDate(d.getDate() + 1);
  }
  return d;
}

/**
 * Session dates for a private lesson (`startDate` + `numberOfSessions`).
 *
 * - **No `daysOfWeek` (empty/absent):** one session per week on the same weekday as `startDate` (legacy).
 * - **With `daysOfWeek`:** first session is the first matching day on or after `startDate`; each following session is the next scheduled weekday (supports multiple sessions per week).
 */
/** One generated session date for a student-initiated private booking, with exception overlay */
export interface PrivateBookingDateEntry {
  date: string;
  available: boolean;
  reason?: string;
}

function isDateInExceptionRange(dateStr: string, ex: CoachAvailabilityException): boolean {
  return dateStr >= ex.dateFrom && dateStr <= ex.dateTo;
}

function isUnavailableForSession(
  dateStr: string,
  sessionStart: string,
  sessionEnd: string,
  exceptions: CoachAvailabilityException[]
): { blocked: boolean; reason?: string } {
  for (const ex of exceptions) {
    if (ex.isAvailable) continue;
    if (!isDateInExceptionRange(dateStr, ex)) continue;
    if (!ex.startTime || !ex.endTime) {
      return { blocked: true, reason: ex.reason ?? 'Coach unavailable' };
    }
    if (doTimesOverlap(sessionStart, sessionEnd, ex.startTime, ex.endTime)) {
      return { blocked: true, reason: ex.reason ?? 'Coach unavailable' };
    }
  }
  return { blocked: false };
}

/** True if a recurring private slot is blocked on a specific calendar date (coach exception). */
export function isPrivateSlotBlockedOnCalendarDate(
  dateYmd: string,
  sessionStart: string,
  sessionEnd: string,
  exceptions: CoachAvailabilityException[]
): boolean {
  return isUnavailableForSession(dateYmd, sessionStart, sessionEnd, exceptions).blocked;
}

/**
 * Weekly recurring dates for student-initiated private bookings, annotated with coach exception conflicts.
 */
export function generatePrivateBookingDates(
  dayOfWeek: number,
  startFromDate: string,
  numberOfSessions: number,
  sessionStart: string,
  sessionEnd: string,
  exceptions: CoachAvailabilityException[] = []
): PrivateBookingDateEntry[] {
  const rawDates = generatePrivateLessonOccurrenceDates(startFromDate, numberOfSessions, [dayOfWeek]);
  return rawDates.map((d) => {
    const dateStr = formatYmd(d);
    const blocked = isUnavailableForSession(dateStr, sessionStart, sessionEnd, exceptions);
    return {
      date: dateStr,
      available: !blocked.blocked,
      reason: blocked.reason,
    };
  });
}

export function generatePrivateLessonOccurrenceDates(
  startDate: DateLike,
  numberOfSessions: number,
  daysOfWeek?: number[] | null
): Date[] {
  if (numberOfSessions <= 0) {
    return [];
  }

  const start = toDate(startDate);
  if (!start) {
    return [];
  }

  if (!daysOfWeek?.length) {
    const anchor = new Date(start);
    anchor.setHours(0, 0, 0, 0);
    return Array.from({ length: numberOfSessions }, (_, i) => {
      const x = new Date(anchor);
      x.setDate(anchor.getDate() + i * 7);
      return x;
    });
  }

  const daySet = new Set(daysOfWeek);
  const dates: Date[] = [];
  let cur = firstDateOnOrAfterMatchingDays(start, daysOfWeek);
  for (let i = 0; i < numberOfSessions; i++) {
    dates.push(new Date(cur));
    if (i < numberOfSessions - 1) {
      cur = nextDateAfterMatchingDays(cur, daySet);
    }
  }
  return dates;
}

/**
 * Generate all individual lesson occurrence dates from a lesson's daysOfWeek
 * and a term's startDate/endDate, merging override status (cancelled/postponed).
 *
 * @param lesson - Lesson with id, daysOfWeek, startTime, endTime
 * @param term - Term with startDate, endDate
 * @param overrides - Optional lesson overrides for this lesson within the term
 * @returns Array of LessonOccurrence for each scheduled date
 */
export function generateLessonOccurrences(
  lesson: Pick<Lesson, 'id' | 'daysOfWeek' | 'startTime' | 'endTime'>,
  term: Pick<Term, 'startDate' | 'endDate'>,
  overrides: LessonOverride[] = []
): LessonOccurrence[] {
  const startDate = toDate(term.startDate);
  const endDate = toDate(term.endDate);

  if (!startDate || !endDate || startDate > endDate) {
    return [];
  }

  const daysOfWeek = lesson.daysOfWeek ?? [];
  if (daysOfWeek.length === 0) {
    return [];
  }

  const occurrences: LessonOccurrence[] = [];
  const current = new Date(startDate);
  current.setHours(0, 0, 0, 0);

  const end = new Date(endDate);
  end.setHours(23, 59, 59, 999);

  while (current <= end) {
    const dayOfWeek = current.getDay();
    if (daysOfWeek.includes(dayOfWeek)) {
      const occurrenceDate = new Date(current);
      const override = overrides.find((o) => {
        const orig = toDate(o.originalDate as DateLike);
        return orig && isSameDate(orig, occurrenceDate);
      });

      let status: LessonOccurrence['status'] = 'scheduled';
      if (override) {
        status = override.overrideType === 'cancelled' ? 'cancelled' : 'postponed';
      }

      occurrences.push({
        date: new Date(occurrenceDate),
        dayOfWeek,
        lessonId: lesson.id,
        startTime: lesson.startTime,
        endTime: lesson.endTime,
        status,
        override,
      });
    }
    current.setDate(current.getDate() + 1);
  }

  return occurrences;
}

/**
 * Generate occurrences for a camp or term-bound lesson (date range + daysOfWeek).
 */
export function generateCampOccurrences(
  lesson: Pick<Lesson, 'id' | 'daysOfWeek' | 'startTime' | 'endTime'>,
  term: Pick<Term, 'startDate' | 'endDate'>,
  overrides: LessonOverride[] = []
): LessonOccurrence[] {
  return generateLessonOccurrences(lesson, term, overrides);
}

/** @deprecated Use generateCampOccurrences */
export const generateClinicOccurrences = generateCampOccurrences;

/** Shift YYYY-MM-DD dates by a number of calendar days (sorted ascending). */
export function shiftYmdDatesByDays(dates: string[], days: number): string[] {
  const out: string[] = [];
  for (const d of dates) {
    const parsed = parseLocalDateYmd(d);
    if (!parsed) continue;
    parsed.setDate(parsed.getDate() + days);
    out.push(formatLessonOccurrenceDateYmd(parsed));
  }
  return [...new Set(out)].sort();
}

export type CampSeriesWeek = {
  weekIndex: number;
  dates: string[];
};

/**
 * Build weekly camp date sets from week-1 explicit days (US-style multi-week camps).
 * Week k shifts week-1 dates forward by 7*(k-1) days.
 */
export function buildCampSeries(week1Dates: string[], weeks: number): CampSeriesWeek[] {
  if (weeks < 1) return [];
  const sorted = [...new Set(week1Dates.filter(Boolean))].sort();
  if (sorted.length === 0) return [];
  return Array.from({ length: weeks }, (_, i) => ({
    weekIndex: i + 1,
    dates: shiftYmdDatesByDays(sorted, 7 * i),
  }));
}

/** Union of weekdays (0=Sun … 6=Sat) from explicit session dates; defaults to Monday when empty. */
export function weekdaysFromSessionDates(dates: string[]): number[] {
  const dows = new Set<number>();
  for (const d of [...dates].sort()) {
    const parsed = parseLocalDateYmd(d);
    if (parsed) dows.add(parsed.getDay());
  }
  const arr = [...dows].sort((a, b) => a - b);
  return arr.length > 0 ? arr : [1];
}

/**
 * Session dates for a single US-style camp week: from startDate through the next 6 calendar days,
 * including only days whose weekday is in daysOfWeek.
 */
export function computeCampWeekSessionDates(startDateYmd: string, daysOfWeek: number[]): string[] {
  const start = parseLocalDateYmd(startDateYmd.trim());
  if (!start || !daysOfWeek?.length) return [];
  const daySet = new Set(daysOfWeek);
  const dates: string[] = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    if (daySet.has(d.getDay())) {
      dates.push(formatLessonOccurrenceDateYmd(d));
    }
  }
  return dates;
}

/** Last camp day in the week window (derived end date for API/storage). */
export function computeCampWeekEndDate(startDateYmd: string, daysOfWeek: number[]): string | null {
  const dates = computeCampWeekSessionDates(startDateYmd, daysOfWeek);
  return dates.length > 0 ? dates[dates.length - 1]! : null;
}

/** Auto-suffix camp name for multi-week series (e.g. "Summer Juniors - Week 2"). */
export function formatCampWeekName(baseName: string, weekIndex: number, weeks: number): string {
  const trimmed = baseName.trim();
  if (weeks <= 1 || weekIndex <= 1) return trimmed;
  return `${trimmed} - Week ${weekIndex}`;
}

/**
 * Whether a calendar day is a schedulable (non-cancelled) occurrence for a term- or camp-bound lesson.
 */
export function isBookableSessionDate(
  lesson: Pick<Lesson, 'id' | 'daysOfWeek' | 'startTime' | 'endTime'>,
  sessionDateYmd: string,
  dateRange: Pick<Term, 'startDate' | 'endDate'>,
  overrides: LessonOverride[] = []
): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(sessionDateYmd)) {
    return false;
  }
  const target = new Date(`${sessionDateYmd}T12:00:00`);
  if (Number.isNaN(target.getTime())) {
    return false;
  }
  const occs = generateLessonOccurrences(lesson, dateRange, overrides);
  return occs.some((occ) => isSameDate(occ.date, target) && occ.status === 'scheduled');
}
